import { IUser, IRole } from '../types/damTypes';

// Define CreateUserData type
export interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '') + '/api';

const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

interface ApiResponse<T> {
  data: T | null; // data can be null on error
  error?: string;
  message?: string;
}

const fetchApi = async <T>(
  url: string, 
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (options.headers) {
    // Merge in other headers, ensuring they are Record<string, string>
    Object.assign(headers, options.headers as Record<string, string>);
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, { ...options, headers });
    // Try to parse JSON, but handle cases where body might be empty (e.g., 204 No Content)
    let responseData: any;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
        responseData = await response.json();
    } else {
        // Handle non-JSON responses if necessary, or assume success if response.ok for non-JSON
        responseData = response.ok ? { message: await response.text() || 'Success' } : { message: await response.text() };
    }

    if (!response.ok) {
      return { 
        data: null,
        error: responseData?.message || `API request failed with status ${response.status}`,
        message: responseData?.message 
      };
    }
    // Ensure data is part of responseData if it was JSON, otherwise responseData itself might be the data (e.g. for text)
    return { data: responseData.data !== undefined ? responseData.data : responseData, message: responseData.message };
  } catch (error: any) {
    console.error('API call error:', error);
    return { data: null, error: error.message || 'Network or parsing error' };
  }
};

export const getUsers = async (): Promise<ApiResponse<IUser[]>> => {
  return fetchApi<IUser[]>(`${API_BASE_URL}/users`);
};

export const getUserById = async (id: string): Promise<ApiResponse<IUser>> => {
  return fetchApi<IUser>(`${API_BASE_URL}/users/${id}`);
};

export const updateUser = async (id: string, userData: Partial<IUser>): Promise<ApiResponse<IUser>> => {
  return fetchApi<IUser>(`${API_BASE_URL}/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  });
};

export const deleteUser = async (id: string): Promise<ApiResponse<{ message: string }>> => {
  return fetchApi<{ message: string }>(`${API_BASE_URL}/users/${id}`, {
    method: 'DELETE',
  });
};

export const updateUserRoles = async (userId: string, roleIds: string[]): Promise<ApiResponse<IUser>> => {
    return fetchApi<IUser>(`${API_BASE_URL}/users/${userId}/roles`, {
        method: 'PUT',
        body: JSON.stringify({ roles: roleIds }),
    });
}; 

export const createUser = async (userData: CreateUserData): Promise<ApiResponse<IUser>> => {
  return fetchApi<IUser>(`${API_BASE_URL}/users`, {
    method: 'POST',
    body: JSON.stringify(userData),
  });
};

export const resetUserPassword = async (
  id: string,
  newPassword: string,
  sendEmail: boolean
): Promise<ApiResponse<{ message: string }>> => {
  return fetchApi<{ message: string }>(`${API_BASE_URL}/users/${id}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ newPassword, sendEmail }),
  });
};